import {
  AfterViewInit,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {Title} from "@angular/platform-browser";
import {registerLocaleData} from "@angular/common";
import {HeaderComponent} from "./base/header/header.component";
import {SidebarBodyComponent} from "./base/sidebar-body/sidebar-body.component";
import {FooterComponent} from "./base/footer/footer.component";
import {TranslatePipe} from "@ngx-translate/core";
import {LogoComponent} from "./base/logo/logo.component";
import {I18nService} from "./services/i18n/i18n.service";
import {NavService} from "./services/nav.service";
import {SettingsService} from "./services/settings.service";
import {SettingKey} from "./base/setting-key.enum";
import {LayoutComponent} from "./pages/user/layout/layout.component";
import {SettingDto} from "./repositories/dto/setting.dto";
import {ToastComponent} from "./base/toast/toast.component";
import {LifecycleBannerComponent} from "./base/lifecycle-banner/lifecycle-banner.component";
import {NoticeBannerComponent} from "./base/notice-banner/notice-banner.component";
import localeDe from '@angular/common/locales/de';
import {StateService} from "./services/state/state.service";
import {interval, Subscription} from "rxjs";
import {isDefined, isFalse, unsubscribe} from "./libs/utils/epic.utils";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faAnglesLeft} from "@fortawesome/free-solid-svg-icons/faAnglesLeft";
import {DeactivateDialogComponent} from "./base/deactivate-guard/deactivate-dialog/deactivate-dialog.component";
import {FormService} from "./services/form.service";
import {HttpClient} from "@angular/common/http";
import {environment} from "../environments/environment";
import {MediaViewerComponent} from "./libs/media/media-viewer/media-viewer.component";
import {BrowserCheckService} from "./services/browser-check.service";
import {BrowserCheckComponent} from "./pages/overview/browser-check/browser-check.component";
import {SupportWidgetComponent} from "./pages/support/support-widget/support-widget.component";
import {UserService} from "./services/user.service";
import {filter, take} from "rxjs/operators";
import {SpinnerComponent} from "./libs/spinner/spinner.component";
import {LoggingService} from "./services/logger/logging.service";
import localeKa from '@angular/common/locales/ka';
import {MediaUploadComponent} from "./libs/media/media-upload/media-upload.component";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {InfoService} from "./services/info.service";
import {IconComponent} from "./libs/icon/icon.component";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    SidebarBodyComponent,
    FooterComponent,
    TranslatePipe,
    LogoComponent,
    ToastComponent,
    MediaViewerComponent,
    FaIconComponent,
    DeactivateDialogComponent,
    BrowserCheckComponent,
    SupportWidgetComponent,
    SpinnerComponent,
    MediaUploadComponent,
    LifecycleBannerComponent,
    NoticeBannerComponent,
    IconComponent
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild("sidebar_main")
  private sidebarMain: ElementRef | undefined;

  protected readonly sidebarIcon: IconDefinition = faAnglesLeft;
  protected readonly supportEnabled: boolean = environment.support.enabled;
  protected sidebarMini: boolean = false;
  protected sidebarOpen: boolean = false;
  protected isDesktop: boolean = true;
  protected browserSupported: boolean = true;
  protected isReady: boolean = false;
  protected connectionLost: boolean = false;

  private readonly titleService: Title = inject(Title);
  private readonly i18n: I18nService = inject(I18nService);
  private readonly navService: NavService = inject(NavService);
  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly stateService: StateService = inject(StateService);
  private readonly formService: FormService = inject(FormService);
  private readonly browserCheck: BrowserCheckService = inject(BrowserCheckService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly userService: UserService = inject(UserService);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly infoService: InfoService = inject(InfoService);

  private readonly document: Document = inject(DOCUMENT);
  private readonly checkVersionIntervalInMs: number = 10 * 60 * 1000; // 10m
  private readonly desktopBreakpointPx: number = 1200; // matches hope-ui.css @media (max-width: 1199.98px)
  private readonly router: Router = inject(Router);
  private readonly systemDarkMode: boolean;
  private readonly subs: Subscription[] = [];
  private readonly isDevSystem: boolean = environment.dev;
  protected readonly isIronBackupViewer: boolean = environment.ironBackupViewer;
  private currentVersion: string | undefined;

  constructor() {
    registerLocaleData(localeDe);
    registerLocaleData(localeKa, 'ka');
    this.browserCheck.getSupported().subscribe((s) => this.browserSupported = s);

    this.systemDarkMode = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    this.isDesktop = window.innerWidth >= this.desktopBreakpointPx;
    this.updateViewportClass();

    this.setLayoutMode(LayoutComponent.MODE_AUTO);
    this.setLayoutColor(LayoutComponent.COLOR_DEFAULT);
    this.setLayoutSidebar(LayoutComponent.SIDEBAR_DEFAULT);
    this.setLayoutDirection(LayoutComponent.DIRECTION_DEFAULT);
    this.prepareLayoutSettings();

    if (isFalse(this.isDevSystem) && isFalse(this.isIronBackupViewer)) {
      this.checkVersion();
      interval(this.checkVersionIntervalInMs).subscribe(() => this.checkVersion());
    }
  }

  ngOnInit(): void {
    this.subs.push(
      this.stateService.getInfo().subscribe((i) => this.titleService.setTitle(i.name)),
      this.userService.getPing().pipe(take(1)).subscribe(() => setTimeout(() => this.isReady = true, 600)),
      this.infoService.getPingError().subscribe((lost) => this.connectionLost = lost),
      // On tablet/mobile the sidebar is an overlay; close it after navigating to a page
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.closeMobileSidebar()),
    );
    // Fallback: in case there are any problems with the ping
    setTimeout(() => {
      if (isFalse(this.isReady)) {
        this.isReady = true;
        this.log.warn('Fallback was used to switch the application to ready.');
      }
    }, 3000);
  }

  ngAfterViewInit(): void {
    this.subs.push(this.i18n.getCurrentLanguageCode().subscribe((lang) => this.setHtmlLanguage(lang)));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  // Save form with Ctrl+S (Strg+S)
  @HostListener('window:keydown.control.s', ['$event'])
  save(event: Event): void {
    this.formService.saveAllForms();
    event.preventDefault();
  }

  @HostListener('window:resize')
  protected onResize(): void {
    const desktop: boolean = window.innerWidth >= this.desktopBreakpointPx;
    if (desktop !== this.isDesktop) {
      this.isDesktop = desktop;
      // Leaving mobile: drop the transient overlay so the persisted desktop state takes over cleanly
      this.sidebarOpen = false;
    }
    this.updateViewportClass();
  }

  // Mirrors the active viewport onto the <body> so global CSS can target it
  // (e.g., body.mobile .foo {}). Component CSS is encapsulated: use :host-context(.mobile) there.
  private updateViewportClass(): void {
    this.document.body.classList.toggle('mobile', isFalse(this.isDesktop));
    this.document.body.classList.toggle('desktop', this.isDesktop);
  }

  // Desktop: toggle the persisted icon-rail. Tablet/mobile: toggle the transient overlay (never persisted).
  protected toggleSidebarState(): void {
    if (this.isDesktop) {
      this.sidebarMini = !this.sidebarMini;
      this.navService.updateSidebarMini(this.sidebarMini);
    } else {
      this.sidebarOpen = !this.sidebarOpen;
    }
  }

  protected closeMobileSidebar(): void {
    if (isFalse(this.isDesktop)) {
      this.sidebarOpen = false;
    }
  }

  private setHtmlLanguage(lang: string): void {
    this.document.documentElement.lang = lang;
  }

  private prepareLayoutSettings(): void {
    this.subs.push(
      this.settingsService.getSettingOnChange(SettingKey.USER_LAYOUT_MODE)
        .subscribe((kv: SettingDto) => this.setLayoutMode(kv.value)),
      this.settingsService.getSettingOnChange(SettingKey.USER_LAYOUT_COLOR)
        .subscribe((kv: SettingDto) => this.setLayoutColor(kv.value)),
      this.settingsService.getSettingOnChange(SettingKey.USER_LAYOUT_SIDEBAR)
        .subscribe((kv: SettingDto) => this.setLayoutSidebar(kv.value)),
      this.settingsService.getSettingOnChange(SettingKey.USER_LAYOUT_DIRECTION)
        .subscribe((kv: SettingDto) => this.setLayoutDirection(kv.value)),
      this.navService.getSidebarMini().subscribe((state) => this.setSidebarMini(state)),
    );
  }

  private setLayoutMode(mode: string | undefined): void {
    if (isDefined(mode)) {
      LayoutComponent.MODE_ALL.forEach((m) => this.document.body.classList.remove(m));
      if (mode === LayoutComponent.MODE_AUTO) {
        const auto: string = this.systemDarkMode ? LayoutComponent.MODE_DARK : LayoutComponent.MODE_LIGHT;
        this.document.body.classList.add(auto);
        this.document.documentElement.setAttribute('data-bs-theme', auto);
      } else {
        this.document.body.classList.add(mode);
        this.document.documentElement.setAttribute('data-bs-theme', mode);
      }
    }
  }

  private setLayoutColor(color: string | undefined): void {
    if (isDefined(color)) {
      LayoutComponent.COLOR_ALL.forEach((c) => this.document.body.classList.remove(LayoutComponent.COLOR_PREFIX + c));
      this.document.body.classList.add(LayoutComponent.COLOR_PREFIX + color);
    }
  }

  private setLayoutSidebar(sidebar: string | undefined): void {
    if (isDefined(sidebar) && isDefined(this.sidebarMain)) {
      // @ts-ignore
      LayoutComponent.SIDEBAR_ALL.forEach((s) => this.sidebarMain.nativeElement.classList.remove(LayoutComponent.SIDEBAR_PREFIX + s));
      this.sidebarMain.nativeElement.classList.add(LayoutComponent.SIDEBAR_PREFIX + sidebar);
    }
  }

  private setLayoutDirection(direction: string | undefined): void {
    if (isDefined(direction)) {
      this.document.documentElement.dir = direction;
    }
  }

  private setSidebarMini(value: boolean | undefined): void {
    if (isDefined(value)) {
      this.sidebarMini = value;
    }
  }

  private checkVersion(): void {
    this.http.get<{ version: string }>("/version.json", {headers: {"Cache-Control": "no-cache"}}).subscribe((data) => {
      if (isDefined(this.currentVersion) && this.currentVersion !== data.version) {
        location.reload();
      }
      this.currentVersion = data.version;
    });
  }
}
